import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../../../config';

const FacebookGlyph = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
        <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z" />
    </svg>
);

const HeartIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
);

const ChatIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const ShareIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
);

const TrendingIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
    </svg>
);

const UsersIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const RefreshIcon = ({ spinning }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        width="14"
        height="14"
        className={spinning ? 'animate-spin' : ''}
    >
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);

const AlertIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const formatNumber = (n) => {
    if (n == null) return '—';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    return String(n);
};

const formatRelative = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.round(d / 7);
    if (w < 5) return `${w}w ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Stat = ({ icon, label, value, accent }) => (
    <div className="flex items-center gap-2.5 min-w-0">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
            {icon}
        </div>
        <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{value}</div>
            <div className="text-[11px] text-gray-500 leading-tight">{label}</div>
        </div>
    </div>
);

const PageHealthCard = ({ integrationId, onReconnect }) => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [needsReconnect, setNeedsReconnect] = useState(false);

    const fetchHealth = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${config.API_BASE_URL}/api/integrations/${integrationId}/page-health`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data?.success) {
                setData(res.data.data);
                setNeedsReconnect(false);
            } else {
                setError(res.data?.message || 'Failed to load page health');
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to load page health';
            setError(msg);
            if (err.response?.data?.requires_reconnect) {
                setNeedsReconnect(true);
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [integrationId]);

    useEffect(() => {
        if (integrationId) fetchHealth(false);
    }, [integrationId, fetchHealth]);

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow-card border border-gray-100/80 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gray-100" />
                    <div className="flex-1 space-y-2">
                        <div className="h-3 bg-gray-100 rounded w-2/3" />
                        <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    {[0, 1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-50 rounded-lg" />)}
                </div>
            </div>
        );
    }

    if (needsReconnect) {
        return (
            <div className="bg-white rounded-2xl shadow-card border border-amber-200/80 p-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                        <AlertIcon />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">Reconnect required</h4>
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">{error}</p>
                        <button
                            onClick={onReconnect}
                            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg text-xs font-semibold shadow-sm hover:shadow-md transition-all"
                        >
                            Reconnect with Facebook
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-2xl shadow-card border border-gray-100/80 p-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                        <AlertIcon />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900">Couldn't load page activity</h4>
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">{error}</p>
                        <button
                            onClick={() => fetchHealth(true)}
                            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-all"
                        >
                            <RefreshIcon /> Try again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const { page, activity, top_post: topPost, recent_posts: recentPosts } = data;

    return (
        <div className="bg-white rounded-2xl shadow-card border border-gray-100/80 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
                {page.picture ? (
                    <img
                        src={page.picture}
                        alt={page.name}
                        className="w-11 h-11 rounded-xl object-cover ring-2 ring-white shadow-sm flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: '#0866FF' }}>
                        <FacebookGlyph />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{page.name}</h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10 flex-shrink-0">
                            Live
                        </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        {page.category && <span className="text-[11px] text-gray-500 truncate">{page.category}</span>}
                        {page.link && (
                            <a href={page.link} target="_blank" rel="noreferrer" className="text-[11px] text-primary-600 hover:underline truncate">
                                Open page →
                            </a>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => fetchHealth(true)}
                    disabled={refreshing}
                    title="Refresh"
                    className="p-2 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-all disabled:opacity-50"
                >
                    <RefreshIcon spinning={refreshing} />
                </button>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-5 py-4 border-b border-gray-100">
                <Stat
                    icon={<UsersIcon />}
                    label="Followers"
                    value={formatNumber(page.fan_count)}
                    accent="bg-blue-50 text-blue-600"
                />
                <Stat
                    icon={<TrendingIcon />}
                    label="Posts (30d)"
                    value={activity.posts_last_30d}
                    accent="bg-violet-50 text-violet-600"
                />
                <Stat
                    icon={<HeartIcon />}
                    label="Avg engagement"
                    value={formatNumber(activity.avg_engagement_per_post)}
                    accent="bg-pink-50 text-pink-600"
                />
                <Stat
                    icon={<ChatIcon />}
                    label="Engagement rate"
                    value={`${activity.engagement_rate_percent}%`}
                    accent="bg-emerald-50 text-emerald-600"
                />
            </div>

            {/* Top post */}
            {topPost && (
                <div className="px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Top performing post</h4>
                        <span className="text-[11px] text-gray-400">{formatRelative(topPost.created_time)}</span>
                    </div>
                    <a
                        href={topPost.permalink_url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex gap-3 items-start group"
                    >
                        {topPost.full_picture && (
                            <img
                                src={topPost.full_picture}
                                alt=""
                                className="w-16 h-16 rounded-lg object-cover flex-shrink-0 ring-1 ring-gray-100"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 line-clamp-2 group-hover:text-gray-900 transition-colors">
                                {topPost.message || <em className="text-gray-400">(media post)</em>}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                <span className="inline-flex items-center gap-1"><HeartIcon /> {formatNumber(topPost.reactions)}</span>
                                <span className="inline-flex items-center gap-1"><ChatIcon /> {formatNumber(topPost.comments)}</span>
                                <span className="inline-flex items-center gap-1"><ShareIcon /> {formatNumber(topPost.shares)}</span>
                            </div>
                        </div>
                    </a>
                </div>
            )}

            {/* Recent posts strip */}
            {recentPosts && recentPosts.length > 0 && (
                <div className="px-5 py-3">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Recent posts</h4>
                        <span className="text-[11px] text-gray-400">{recentPosts.length} of {activity.posts_fetched}</span>
                    </div>
                    <ul className="space-y-2">
                        {recentPosts.map(p => (
                            <li key={p.id} className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="text-gray-400 flex-shrink-0">•</span>
                                <a
                                    href={p.permalink_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex-1 min-w-0 truncate hover:text-primary-600"
                                    title={p.message}
                                >
                                    {p.message || '(media post)'}
                                </a>
                                <span className="flex-shrink-0 inline-flex items-center gap-1 text-gray-400">
                                    <HeartIcon /> {formatNumber(p.reactions + p.comments + p.shares)}
                                </span>
                                <span className="flex-shrink-0 text-[10px] text-gray-400 w-12 text-right">{formatRelative(p.created_time)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default PageHealthCard;
